import { Player } from '@lottiefiles/react-lottie-player';
import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface LottieAnimationProps {
  src: string;
  trigger?: 'load' | 'hover' | 'scroll' | 'all';
  loop?: boolean;
  autoplay?: boolean;
  speed?: number;
  className?: string;
  fallback?: React.ReactNode;
  style?: React.CSSProperties;
}

export default function LottieAnimation({
  src,
  trigger = 'load',
  loop = true,
  autoplay = true,
  speed = 1,
  className = '',
  fallback = null,
  style = {}
}: LottieAnimationProps) {
  const [, setIsVisible] = useState(false);
  const [shouldPlay, setShouldPlay] = useState(autoplay && trigger === 'load');
  const [hasError, setHasError] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for scroll trigger
  useEffect(() => {
    if (trigger === 'scroll' || trigger === 'all') {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (trigger === 'scroll' || (trigger === 'all' && !shouldPlay)) {
              setShouldPlay(true);
              playerRef.current?.play();
            }
          }
        },
        { threshold: 0.3 }
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => observer.disconnect();
    }
  }, [trigger, shouldPlay]);

  // Handle hover events
  const handleMouseEnter = () => {
    if (trigger === 'hover' || trigger === 'all') {
      setShouldPlay(true);
      playerRef.current?.play();
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover' && !loop) {
      setShouldPlay(false);
      playerRef.current?.stop();
    }
  };

  // Handle animation errors
  const handleError = () => {
    setHasError(true);
    console.error('Lottie animation failed to load:', src);
    console.error('Falling back to static content');
  };


  if (hasError && fallback) {
    return <div className={className} style={style}>{fallback}</div>;
  }

  if (prefersReducedMotion && fallback) {
    return <div className={className} style={style}>{fallback}</div>;
  }

  return (
    <div
      ref={containerRef}
      className={`lottie-container ${className}`}
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="img"
      aria-label="Animated illustration"
    >
      <Player
        ref={playerRef}
        src={src}
        autoplay={shouldPlay}
        loop={loop}
        speed={speed}
        style={{ width: '100%', height: '100%' }}
        onEvent={(event) => {
          if (event === 'error') {
            handleError();
          }
        }}
      />
    </div>
  );
}